import { ref, computed } from 'vue';
import store from '@/store';

/**
 * Navigation level types and their labels.
 * Each level corresponds to a view in the drill-down UI.
 */
const LEVEL_TYPES = {
  collections: 'collections',
  frames: 'frames',
  options: 'options',
  optionDetail: 'optionDetail',
  display: 'display',
};

/**
 * Composable for Canva-style drill-down navigation.
 * Instead of a tree, uses a navigation stack where each level
 * is a full-screen card grid. Click a card to go deeper,
 * breadcrumbs to go back.
 *
 * Stack structure: [{type, id, title, data}]
 * - Root: [{type: 'collections', title: 'Collections'}]
 * - After clicking a collection:
 *   [{type: 'collections', ...}, {type: 'frames', id: 5, title: 'Artisanal', data: {...}}]
 */
// eslint-disable-next-line import/prefer-default-export
export function useDrillDown() {
  // Navigation stack — always starts with collections root
  const stack = ref([
    {
      type: LEVEL_TYPES.collections,
      id: null,
      title: 'Collections',
      data: null,
    },
  ]);

  // Options cache per frame
  const optionsCache = new Map();

  function buildOptionTitle(option) {
    const size = option.frameSize?.name || '';
    const orientation = option.frameOrientation || '';
    const color = option.frameColor?.name || 'n/a';
    return `${size} ${orientation} ${color}`.trim();
  }

  // Current breadcrumbs computed from stack
  const breadcrumbs = computed(() => stack.value.map((level, idx) => ({
    label: level.title,
    index: idx,
    type: level.type,
  })));

  // Current level — the top of the stack
  const currentLevel = computed(() => stack.value[stack.value.length - 1]);

  // Current level type for v-if switches in the template
  const currentType = computed(() => currentLevel.value?.type);

  // --- Navigation methods ---

  /**
   * Drill into a collection — show its frames
   */
  async function openCollection(collection) {
    stack.value.push({
      type: LEVEL_TYPES.frames,
      id: collection.id,
      title: collection.name,
      data: collection,
    });
  }

  /**
   * Drill into a frame — load its options and show grouped by size
   */
  async function openFrame(frame) {
    // Load options if not cached
    if (!optionsCache.has(frame.id)) {
      const frameName = frame.name || 'photo_art';
      await store.dispatch('createTemplate/getFrameOptions', {
        id: frame.id,
        type: frameName,
      });
      const options = store.state.createTemplate.frameOptions || [];
      optionsCache.set(frame.id, [...options]);
    }

    // Also load frame details, sizes, colors for the options view
    await Promise.all([
      store.dispatch('createTemplate/getFrameDetails', frame.id),
      store.dispatch('createTemplate/getFrameSizes'),
      store.dispatch('createTemplate/getFrameColors'),
    ]);

    stack.value.push({
      type: LEVEL_TYPES.options,
      id: frame.id,
      title: frame.displayName || frame.name,
      data: frame,
    });
  }

  /**
   * Drill into a specific option — show its settings and displays
   */
  async function openOption(option) {
    // Load displays for this option
    await store.dispatch('createTemplate/getFrameOptionDisplays', {
      productId: option.id,
    });

    stack.value.push({
      type: LEVEL_TYPES.optionDetail,
      id: option.id,
      title: buildOptionTitle(option),
      data: option,
    });
  }

  /**
   * Drill into a specific display — show the visual constructor
   */
  async function openDisplay(display, parentOption) {
    // Ensure displays are loaded for the parent option
    if (parentOption) {
      await store.dispatch('createTemplate/getFrameOptionDisplays', {
        productId: parentOption.id,
      });
    }

    stack.value.push({
      type: LEVEL_TYPES.display,
      id: display.id,
      title: display.name,
      data: { display, option: parentOption },
    });
  }

  /**
   * Navigate back to a specific level via breadcrumb click.
   * Pops everything above the target index.
   */
  function navigateTo(index) {
    if (index < stack.value.length - 1) {
      stack.value = stack.value.slice(0, index + 1);
    }
  }

  /**
   * Go back one level
   */
  function goBack() {
    if (stack.value.length > 1) {
      stack.value.pop();
    }
  }

  /**
   * Refresh current level — used after CRUD operations.
   * Clears relevant caches and re-fetches data.
   */
  async function refreshCurrentLevel() {
    const current = currentLevel.value;
    if (!current) return;

    switch (current.type) {
      case LEVEL_TYPES.collections:
        await store.dispatch('createTemplate/getTemplateCollections');
        break;
      case LEVEL_TYPES.frames:
        await store.dispatch(
          'createTemplate/getTemplateCollectionsFrames',
        );
        break;
      case LEVEL_TYPES.options:
        optionsCache.delete(current.id);
        await store.dispatch('createTemplate/getFrameOptions', {
          id: current.id,
          type: current.data?.name || 'photo_art',
        });
        optionsCache.set(
          current.id,
          [...(store.state.createTemplate.frameOptions || [])],
        );
        break;
      case LEVEL_TYPES.optionDetail:
        await store.dispatch('createTemplate/getFrameOptionDisplays', {
          productId: current.id,
        });
        break;
      default:
        break;
    }
  }

  /**
   * Get cached options for a frame (used by OptionsGrid)
   */
  function getCachedOptions(frameId) {
    return optionsCache.get(frameId)
      || store.state.createTemplate.frameOptions
      || [];
  }

  return {
    stack,
    breadcrumbs,
    currentLevel,
    currentType,
    LEVEL_TYPES,
    openCollection,
    openFrame,
    openOption,
    openDisplay,
    navigateTo,
    goBack,
    refreshCurrentLevel,
    getCachedOptions,
  };
}
